<script setup lang="ts">
import DishImage from '@/components/Menus/DishImage.vue';
import LoadingBar from '@/components/Skeletons/LoadingBar.vue';
import { useEventBus } from '@/composables/useEventBus';
import { useFetch } from '@/composables/useFetch';
import { useOnboarding } from '@/composables/useOnboarding';
import type { MessageSchema } from '@/i18n';
import { mockDishes } from '@/onboarding/mockData';
import { useDishesStore } from '@/stores/dishesStore';
import { useEnvStore } from '@/stores/envStore';
import type { Dish } from '@/types/dish';
import { useToast } from 'primevue/usetoast';
import { nextTick, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import DishImageSkeleton from '../../components/Skeletons/DishImageSkeleton.vue';

const { t } = useI18n<{ message: MessageSchema }>({ useScope: 'global' });

const route = useRoute();
const router = useRouter();
const toast = useToast();
const envStore = useEnvStore();
const dishesStore = useDishesStore();
const dish = ref<Dish | null>(dishesStore.selectedDish);
dish.value = dishesStore.selectedDish;
const eventBus = useEventBus();
const isError = ref(false);
const { fetchData } = useFetch();
const { isOnboardingActive } = useOnboarding();

// mounted
onMounted(async () => {
  const dishIdParam = parseInt(route.params.id.toString());
  console.log('[onMounted] ID du plat chargé :', dishIdParam);

  if (isOnboardingActive.value && dishIdParam < 0) {
    console.log('[onMounted] Onboarding actif pour ID négatif.');

    const foundMock = mockDishes.find((d) => d.id === dishIdParam);
    dish.value = foundMock || mockDishes[0];
    dishesStore.selectedDish = dish.value;
    console.log('[onMounted] Plat mock chargé :', dish.value);

    // Vérifier si les images existent déjà
    if (!dish.value?.image_url_gastro || !dish.value?.image_url_bistro) {
      console.log('[onMounted] Images manquantes, génération nécessaire.');
      dishesStore.generateImages(dish.value.id, toast);
    }

    return;
  }

  dish.value = null;
  const { data } = await fetchData(`${envStore.apiUrl}/dishes/${route.params['id']}`);
  console.log('[onMounted] Données récupérées depuis l’API :', data);

  if (data) {
    dishesStore.selectedDish = data;
    dish.value = data;
    console.log('[onMounted] Plat sélectionné mis à jour :', dish.value);
  } else {
    console.log('[onMounted] Erreur : redirection vers la page du plat.');
    router.push('/menu/dishes/' + route.params['id']);
  }
});

// Emit onboarding event when images are generated
watch(
  () => dishesStore.generatingImage,
  async (isGenerating, prevGenerating) => {
    if (prevGenerating && !isGenerating) {
      await nextTick();
      eventBus.emit('onboarding:generated-images-ready');
    }
  },
);

async function changeSelectedImage(newlySelectedImage: 'user' | 'gastro' | 'bistro') {
  if (!dish.value) return;

  const success = await dishesStore.changeSelectedImage(dish.value.id, newlySelectedImage);
  if (success && dish.value.selected_image !== 'none') {
    router.back();
  }
}

function regenerateImages() {
  if (!dishesStore.selectedDish) {
    console.log('[regenerateImages] Aucun plat sélectionné.');
    return;
  }

  console.log('[regenerateImages] Plat sélectionné :', dishesStore.selectedDish);

  if (dishesStore.selectedDish.id < 0) {
    const staticImagesMap = {
      '-1': {
        gastro: '/images/onboarding/parmentier-gastro.webp',
        bistro: '/images/onboarding/parmentier-bistro.webp',
      },
    };

    const staticImages = staticImagesMap[dishesStore.selectedDish.id];

    if (staticImages) {
      dishesStore.selectedDish.image_url_gastro = staticImages.gastro;
      dishesStore.selectedDish.image_url_bistro = staticImages.bistro;

      // Synchroniser avec dish.value
      if (dish.value) {
        dish.value.image_url_gastro = staticImages.gastro;
        dish.value.image_url_bistro = staticImages.bistro;
      }

      console.log('[regenerateImages] Images statiques assignées :', {
        gastro: dishesStore.selectedDish.image_url_gastro,
        bistro: dishesStore.selectedDish.image_url_bistro,
      });

      toast.add({
        severity: 'success',
        summary: t('common.success'),
        detail: t('menuDishView.staticImagesLinked'),
        life: 3000,
      });
    } else {
      console.log('[regenerateImages] Aucun mapping trouvé pour l’ID négatif :', dishesStore.selectedDish.id);

      toast.add({
        severity: 'warn',
        summary: t('common.warning'),
        detail: t('menuDishView.noStaticImages'),
        life: 3000,
      });
    }
    return;
  }

  console.log('[regenerateImages] Génération d’images via API pour l’ID :', dishesStore.selectedDish.id);
  dishesStore.generateImages(dishesStore.selectedDish.id, toast);
}

watch(
  () => dishesStore.selectedDish?.image_url_gastro,
  (newImageUrl) => {
    console.log('[watch] Nouvelle URL d’image gastro :', newImageUrl);
  },
);

watch(
  () => dishesStore.selectedDish?.image_url_bistro,
  (newImageUrl) => {
    console.log('[watch] Nouvelle URL d’image bistro :', newImageUrl);
  },
);
</script>

<template>
  <main>
    <h2 class="mb-2">{{ dish ? dish.name : t('common.loading') }}</h2>
    <div v-if="!dishesStore.generatingImage" class="my-4 hover:cursor-pointer" @click="regenerateImages">
      <p><i class="pi pi-replay"></i> {{ t('menuDishView.regenerateImage') }}</p>
    </div>
    <LoadingBar v-if="dishesStore.generatingImage" class="my-5" :duration-in-seconds="30" />
    <div v-if="dishesStore.generatingImage" class="onboarding-wait-generated-images">
      <h3>{{ t('menuDishView.image.gastronomicImage') }}</h3>
      <div class="flex justify-center">
        <DishImageSkeleton />
      </div>
      <h3 class="mb-1 mt-10">{{ t('menuDishView.image.bistronomicImage') }}</h3>
      <div class="flex justify-center">
        <DishImageSkeleton />
      </div>
    </div>
    <div v-if="!dishesStore.generatingImage" class="onboarding-generated-images">
      <div v-if="dish?.image_url_gastro">
        <h3>{{ t('menuDishView.image.gastronomicImage') }}</h3>
        <div class="mt-2 flex flex-row justify-center gap-3">
          <DishImage
            :dish-id="dish?.id"
            type="gastro"
            :alt="dish?.name"
            :selected="dish.selected_image === 'gastro'"
            @handle-select="changeSelectedImage('gastro')"
          />
          <!-- <div class="flex flex-col gap-2">
            <Button icon="pi pi-replay" rounded aria-label="Regenerate" />
            <Button icon="pi pi-trash" severity="danger" rounded aria-label="Delete" />
          </div> -->
        </div>
      </div>
      <div v-if="dish?.image_url_bistro">
        <h3 class="mb-1 mt-10">{{ t('menuDishView.image.bistronomicImage') }}</h3>
        <div class="mt-2 flex flex-row justify-center gap-3">
          <DishImage
            :dish-id="dish?.id"
            type="bistro"
            :alt="dish?.name"
            :selected="dish.selected_image === 'bistro'"
            @handle-select="changeSelectedImage('bistro')"
          />
          <!-- <div class="flex flex-col gap-2">
            <Button icon="pi pi-replay" rounded aria-label="Regenerate" />
            <Button icon="pi pi-trash" severity="danger" rounded aria-label="Delete" />
          </div> -->
        </div>
      </div>
      <div v-if="dish?.image_url_user">
        <h3 class="mb-1 mt-10">{{ t('menuDishView.image.customImage') }}</h3>
        <div class="mt-2 flex flex-row justify-center gap-3">
          <DishImage
            :dish-id="dish?.id"
            type="user"
            :alt="dish?.name"
            :selected="dish.selected_image === 'user'"
            @handle-select="changeSelectedImage('user')"
          />
          <!-- <div class="flex flex-col gap-2">
            <Button icon="pi pi-replay" rounded aria-label="Regenerate" />
            <Button icon="pi pi-trash" severity="danger" rounded aria-label="Delete" />
          </div> -->
        </div>
      </div>
      <p v-else-if="isError">{{ t('menuImage.imageLoadError') }}</p>
    </div>
    <div class="mt-2 flex justify-end gap-2">
      <!-- TODO : put back buttons once ready-->
      <!-- <Button rounded link :pt="{ root: { class: '!p-1.5 !w-8 !h-8' } }" :pt-options="{ mergeProps: true }"
        @click="console.log('reload')">
        <template #icon>
          <ButtonRefresh />
        </template>
</Button>
<Button rounded link :pt="{ root: { class: '!p-1.5 !w-8 !h-8' } }" :pt-options="{ mergeProps: true }"
  @click="console.log('share')">
  <template #icon>
          <ButtonShare />
        </template>
</Button> -->
    </div>
  </main>
</template>
